import 'regenerator-runtime/runtime';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  getInfosFromDeltafaktoren,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isAngebotsIdEqual,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSpartePayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  addVorbelegteVorschaedenToTaasRequestModel,
  App,
  getTarifbereich,
  Produktlinie,
  VermietetesBebautesObjekt,
  VermietetesObjekt,
  VermietetesObjektEnum,
  VermietetesObjektQuoteDB,
  VermietetesUnbebautesObjekt,
} from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForShop,
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getSelectedProduktlinie,
  getTaasVertragsoptionen,
} from '@ps20-frontend/preis-lib';
import { Dialogschritt, EinheitenArt, TaasRequestModel, TaasRsData } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { RsActions } from '../rsActions';
import { IAppState } from '../appState';
import {
  buildTaasRsDataForShop,
  buildTaasRSDataForStrecke,
} from './transformer/rsData.transformer';

export function* watchQuoteDB() {
  yield takeLatest(GlobalActions.QUOTEDB_DATA_REQUESTED, safeSaga(getQuoteDbData, App.RS));
  yield takeLatest(RsActions.QUOTEDB_REQUESTED_RS, safeSaga(quoteDbInit, App.RS));
}

export function* getQuoteDbData(action: GlobalAction<QuoteDbRequestPayload>) {
  const angebotsIds: AngebotsId[] = action.payload.angebotsIds;
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  let taasRequestModel: TaasRequestModel<TaasRsData>;
  let selectedProduktlinie: Produktlinie;
  let alleProduktlinien: Array<Produktlinie>;
  const spartenAngebotsId: AngebotsId = { appId: App.RS };
  if (angebotsIds && !angebotsIds.some(angebot => isAngebotsIdEqual(spartenAngebotsId, angebot))) {
    yield cancel();
    return;
  }

  if (
    (state.produkt && state.gesamtbeitrag !== 0) ||
    state.transientState.fehlerCodeTaas ||
    (state.vertragsoptionen?.beibehalten && state.gesamtbeitragMitRabatt !== 0)
  ) {
    selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    alleProduktlinien = state.produkt.produktlinien;
    if (action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
      taasRequestModel = yield call(buildTaasRequestModel, state);
    } else {
      taasRequestModel = yield call(buildTaasRequestModelShop, state);
    }
    taasRequestModel = addVorbelegteVorschaedenToTaasRequestModel(
      taasRequestModel,
      state.transientState.vorvertragVorschaeden,
      'anzVorschaeden',
    );
  }

  const quoteDbPayload: QuoteDbSpartePayload<TaasRsData> = {
    angebotsId: { appId: App.RS },
    taasSparteRequestModel: taasRequestModel,
    selectedProduktlinie,
    alleProduktlinien,
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    gesamtpreis:
      action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN
        ? state.gesamtbeitrag
        : state.gesamtbeitragMitRabatt,
    vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
    geschaeftsfuehrer: state.selbststaendigkeit.geschaeftsfuehrer,
    antragsnummer: state.antragsnummer,
    vermietetesObjekt: convertVermieteteObjekteQuoteDb(state),
  };

  getInfosFromDeltafaktoren(state.taasInfo, state.vertragsoptionen, quoteDbPayload);

  yield call(globalPut.put, new GlobalActions().quoteDBDataSparteGenerated(quoteDbPayload));
}

export function* quoteDbInit() {
  const globalPut = GlobalPut.getInstance();
  yield take(GlobalActions.GESAMTPREIS_CHANGED);
  const state: IAppState = yield select();
  const fehlerCodeTaas = state.transientState.fehlerCodeTaas;
  if (fehlerCodeTaas === null) {
    yield cancel();
    return;
  }
  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: { appId: App.RS },
    dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
    fehlerCodeTaas,
  };
  yield call([globalPut, globalPut.put], new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

export function buildTaasRequestModel(state: IAppState): TaasRequestModel<TaasRsData> {
  const taasData: TaasRsData = buildTaasRSDataForStrecke(state);
  return buildTaasRequestModelForStrecke(
    getTaasVertragsoptionen(state.vertragsoptionen),
    taasData,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function buildTaasRequestModelShop(state: IAppState): TaasRequestModel<TaasRsData> {
  const rsData: TaasRsData = buildTaasRsDataForShop(state);

  const newHashedTaasRequest = createHashedTaasData(rsData);
  return buildTaasRequestModelForShop(
    rsData,
    newHashedTaasRequest,
    getTaasVertragsoptionen(state.vertragsoptionen),
    state.taasInfo,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function convertVermieteteObjekteQuoteDb(state: IAppState): Array<VermietetesObjektQuoteDB> {
  const vermieteteObjekte: Array<VermietetesObjekt> = state.vermieteteObjekte;
  if (!vermieteteObjekte) {
    return;
  }
  const vermieteteEinheiten: Array<VermietetesObjektQuoteDB> =
    new Array<VermietetesObjektQuoteDB>();
  vermieteteObjekte.forEach(vermietetesObjektFrontend => {
    const bebaut = vermietetesObjektFrontend.grundstueckBebaut === VermietetesObjektEnum.BEBAUT;
    if (bebaut) {
      const vermietetesGebauede: VermietetesBebautesObjekt = vermietetesObjektFrontend;
      if (vermietetesGebauede.bruttojahresmieteWohneinheiten) {
        vermieteteEinheiten.push({
          id: vermieteteObjekte.indexOf(vermietetesGebauede) + 1,
          plzSachort: vermietetesObjektFrontend.plz,
          jahresBruttoMiete: vermietetesGebauede.bruttojahresmieteWohneinheiten,
          einheitenArt: EinheitenArt.WOHNEINHEIT,
          anzahlVermieteteEinheiten: vermietetesGebauede.wohneinheiten,
        });
      }
      if (vermietetesGebauede.bruttojahresmieteGewerbeeinheiten) {
        vermieteteEinheiten.push({
          id: vermieteteObjekte.indexOf(vermietetesGebauede) + 1,
          plzSachort: vermietetesObjektFrontend.plz,
          jahresBruttoMiete: vermietetesGebauede.bruttojahresmieteGewerbeeinheiten,
          einheitenArt: EinheitenArt.GEWERBEEINHEIT,
          anzahlVermieteteEinheiten: vermietetesGebauede.gewerbeeinheiten,
        });
      }
      if (vermietetesGebauede.bruttojahresmieteStellplaetze) {
        vermieteteEinheiten.push({
          id: vermieteteObjekte.indexOf(vermietetesGebauede) + 1,
          plzSachort: vermietetesObjektFrontend.plz,
          jahresBruttoMiete: vermietetesGebauede.bruttojahresmieteStellplaetze,
          einheitenArt: EinheitenArt.GARAGE,
          anzahlVermieteteEinheiten: vermietetesGebauede.stellplaetze,
        });
      }
    } else {
      const vermietetesGruendstuck: VermietetesUnbebautesObjekt = vermietetesObjektFrontend;
      vermieteteEinheiten.push({
        id: vermieteteObjekte.indexOf(vermietetesGruendstuck) + 1,
        plzSachort: vermietetesGruendstuck.plz,
        einheitenArt: EinheitenArt.UNBEBAUTES_GRUND_STUECK,
        unbebauteFlaeche: vermietetesGruendstuck.grundstueckFlaeche,
      });
    }
  });
  return vermieteteEinheiten;
}
